import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Link,
  Chip,
  Tooltip,
  IconButton,
  Button,
} from "@mui/material";
import { useHMG } from "../../../HMGContext";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LockIcon from "@mui/icons-material/Lock";
import { Icon } from "@iconify/react";

const ordinalWordMap = { First: "1st", Second: "2nd", Third: "3rd", Fourth: "4th", Last: "last" };

const toOrdinalLabel = (word) => ordinalWordMap[word] || word?.toLowerCase() || word;

const toOrdinalSuffix = (numStr) => {
  const n = parseInt(numStr, 10);
  if (Number.isNaN(n)) return numStr;
  const suffix =
    n % 10 === 1 && n % 100 !== 11 ? "st"
    : n % 10 === 2 && n % 100 !== 12 ? "nd"
    : n % 10 === 3 && n % 100 !== 13 ? "rd"
    : "th";
  return `${n}${suffix}`;
};

const toMonthLabel = (monthStr) => {
  if (!monthStr) return monthStr;
  const parts = monthStr.split(" ");
  if (parts.length === 2) return `${toOrdinalLabel(parts[0])} ${parts[1].toLowerCase()}`;
  return monthStr.toLowerCase();
};

// Format critique template publish date
const formatCritiquePublishDate = (publishDate) => {
  if (!publishDate || !publishDate.data?.param1 || !publishDate.period) {
    return null;
  }
  const { param1, param2, param3, is_configurable } = publishDate.data;
  const period = publishDate.period.charAt(0).toUpperCase() + publishDate.period.slice(1);

  // Weekly: "Weekly on Friday" (param1: "Friday")
  if (publishDate.period === "weekly") {
    return `Weekly on ${param1}`;
  }

  // Monthly
  if (publishDate.period === "monthly") {
    if (is_configurable) {
      // Monthly on 1st Sunday (param1: "First", param2: "Sunday")
      return `Monthly on ${toOrdinalLabel(param1)} ${param2}`;
    } else {
      // Monthly on 20th (param1: "20")
      return `Monthly on ${toOrdinalSuffix(param1)}`;
    }
  }

  // Quarterly
  if (publishDate.period === "quarterly") {
    if (is_configurable) {
      // Quarterly on 2nd month on 1st Sunday (param1: "First", param2: "Sunday", param3: "Second month")
      return `Qtr: ${toMonthLabel(param3)}, ${toOrdinalLabel(param1)} ${param2?.toLowerCase() || param2}`;
    } else {
      // Quarterly on 1st month on 20th (param1: "20", param2: "First month")
      return `Qtr: ${toMonthLabel(param2)}, ${toOrdinalSuffix(param1)}`;
    }
  }

  // Fallback for other periods
  return is_configurable
    ? `${period} on the ${toOrdinalLabel(param1)} ${param2}`
    : `${period} on the ${toOrdinalSuffix(param1)} date`;
};

// Format critique template due date
const formatCritiqueDueDate = (dueDate, publishDate) => {
  if (!dueDate || !dueDate.data?.param1 || !publishDate?.period) {
    return null;
  }
  const { param1, param2, param3, is_configurable } = dueDate.data;
  const period = publishDate.period.charAt(0).toUpperCase() + publishDate.period.slice(1);

  // Weekly: "Weekly on Friday" (param1: "Friday")
  if (publishDate.period === "weekly") {
    return `Weekly on ${param1}`;
  }

  // Monthly
  if (publishDate.period === "monthly") {
    if (is_configurable) {
      // Monthly on 1st Sunday (param1: "First", param2: "Sunday")
      return `Monthly on ${toOrdinalLabel(param1)} ${param2}`;
    } else {
      // Monthly on 20th Date (param1: "20")
      return `Monthly on ${toOrdinalSuffix(param1)}`;
    }
  }

  // Quarterly
  if (publishDate.period === "quarterly") {
    if (is_configurable) {
      // Quarterly on 2nd month on 1st Sunday (param1: "First", param2: "Sunday", param3: "Second month")
      return `Qtr: ${toMonthLabel(param3)}, ${toOrdinalLabel(param1)} ${param2?.toLowerCase() || param2}`;
    } else {
      // Quarterly on 1st month on 20th date (param1: "20", param2: "First month")
      return `Qtr: ${toMonthLabel(param2)}, ${toOrdinalSuffix(param1)} date`;
    }
  }

  // Fallback for other periods
  return is_configurable
    ? `${period} on the ${toOrdinalLabel(param1)} ${param2}`
    : `${period} on the ${toOrdinalSuffix(param1)}`;
};

const CritiqueTemplateDetailsHeader = ({
  templateData,
  showHeaderBtn,
  onSettingsClick,
  showReadOnlyLabel = false,
}) => {
  const navigate = useNavigate();
  const {isSidebarOpen} = useHMG()

  const handleBackToTemplates = () => {
    navigate("/critique/templates");
  };

  const handleSettingsClick = () => {
    if (onSettingsClick) {
      onSettingsClick();
    }
  };

  return (
    <Box display="flex" gap={"2px"} flexDirection={"column"}>
      {/* Breadcrumbs */}
      <Box
        sx={{
          "& .MuiTypography-root": {
            fontSize: "14px",
          },
        }}
      >
        <Breadcrumbs
          separator="•"
          sx={{
            "& .MuiBreadcrumbs-separator": {
              fontSize: "26px",
              margin: "0 16px",
              display: "inline-block",
              lineHeight: "0",
            },
          }}
          aria-label="breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            style={{ cursor: "pointer" }}
            onClick={handleBackToTemplates}
            className="!text-sm !leading-[22px] !text-[#637381]"
          >
            Critique templates
          </Link>
        </Breadcrumbs>
      </Box>

      {/* Title and Chips */}
      <Box
        display="flex"
        flexDirection={"row"}
        alignItems={"center"}
        height={24}
        sx={{
          "& .MuiChip-root": {
            borderRadius: "6px",
            fontWeight: 700,
          },
        }}
        className="!gap-[8px]"
      >
        {/* Template Title */}
        {(() => {
          const fullName = templateData?.critique_name || "Untitled Template";
          const maxLength = isSidebarOpen?15:33;
          const isTruncated = fullName.length > maxLength;
          const displayName = isTruncated ? fullName.substring(0, maxLength) + "..." : fullName;

          const typographyContent = (
            <h1 className="text-2xl leading-[36px] text-[#212B36] font-bold flex-shrink-0">
              {displayName}
            </h1>
          );

          // Only show tooltip if the name is truncated
          if (isTruncated) {
            return (
              <Tooltip
                title={fullName}
                arrow
                placement="top"
                slotProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                    },
                  },
                }}
              >
                {typographyContent}
              </Tooltip>
            );
          }

          return typographyContent;
        })()}
        {/* Status Chip - click opens Settings */}
        <Tooltip
          title="Settings"
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: "#212B36",
                "& .MuiTooltip-arrow": {
                  color: "#212B36",
                },
              },
            },
          }}
        >
          <Chip
            size="small"
            sx={{
              height: 24,
              cursor: "pointer",
              // "&:hover": {
              //   opacity: 0.9,
              // },
            }}
            className={`${
              templateData?.is_published
                ? "!text-[#118D57] !bg-[#22C55E]/[.16]"
                : "!text-[#637381] !bg-[#919EAB]/[.16]"
            } !text-[12px] !leading-[20px]`}
            label={templateData?.is_published ? "Published" : "Draft"}
            onClick={handleSettingsClick}
          />
        </Tooltip>
        {/* Publish Date Chip */}
        {formatCritiquePublishDate(templateData?.publish_date) && (
          <Chip
            size="small"
            sx={{height:24}}
            className="!text-[#006C9C] !bg-[#00B8D929]/[.16] !text-[12px] !leading-[20px]"
            icon={
              <Icon
                icon="solar:plain-bold-duotone"
                width={16}
                height={16}
                style={{ color: "#006C9C" }}
              />
            }
            label={formatCritiquePublishDate(templateData?.publish_date)}
          />
        )}
        {/* Due Date Chip */}
        {formatCritiqueDueDate(templateData?.due_date, templateData?.publish_date) && (
          <Chip
            size="small"
            className="!text-[#006C9C] !bg-[#00B8D929]/[.16] !text-[12px] !leading-[20px]"
            sx={{height:24}}
            icon={
              <Icon
                icon="solar:alarm-bold-duotone"
                width={16}
                height={16}
                style={{ color: "#006C9C" }}
              />
            }
            label={formatCritiqueDueDate(templateData?.due_date, templateData?.publish_date)}
          />
        )}
        {/* Settings Icon */}
        {showReadOnlyLabel && (
          <Chip
            size="small"
            icon={<LockIcon sx={{ fontSize: 14, color: "#637381 !important" }} />}
            label="Read only"
            sx={{ height: 24 }}
            className="!text-[#637381] !bg-[#919EAB]/[.16] !text-[12px] !leading-[20px]"
          />
        )}
        <Tooltip
          title="Settings"
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: "#212B36",
                "& .MuiTooltip-arrow": {
                  color: "#212B36",
                },
              },
            },
          }}
          PopperProps={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, -9],
                },
              },
            ],
          }}
        >
          <IconButton
            className="border bg-white p-1.5"
            style={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              borderColor: "#919EAB52",
            }}
            sx={{
              border: "1px solid #D1D5DB",
              borderRadius: 1,
              padding: "4px",
              "&.Mui-disabled": {
                borderColor: "#E5E7EB",
                color: "#919EAB",
              },
            }}
            onClick={handleSettingsClick}
            disabled={showReadOnlyLabel}
          >
            <Icon
              icon="solar:settings-bold-duotone"
              width={18}
              height={18}
              style={{ color: showReadOnlyLabel ? "#919EAB" : "#212B36" }}
            />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default CritiqueTemplateDetailsHeader;

